import { NgClass } from '@angular/common';
import {
  Component,
  ElementRef,
  HostBinding,
  Inject,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  AiFeaturesService,
  LANGFUSE_SYSTEM_TAG,
  MaximizerAiService,
  PIIContext,
  PiiAnonymizationService,
  SYSTEM_PROMPT_ANONYMIZED_CONSIDERATIONS,
  SYSTEM_PROMPT_CHANGE_LANGUAGE,
} from '@maximizer/ai/data-access';
import {
  AddressBookService,
  ContextService,
  LocaleService,
  OpenAiService,
  SessionService,
} from '@maximizer/core/shared/data-access';
import {
  CDN_URL,
  Entity,
  EntityEvent,
  EntityType,
  FieldSet,
  InteractionEvent,
  NoteEvent,
  Octopus,
  OpenAI,
  Session,
  TranslationListItem,
} from '@maximizer/core/shared/domain';
import { EmbeddedComponent } from '@maximizer/core/shared/ui/components/base';
import { UI_LOADER } from '@maximizer/core/shared/ui/components/loader';
import {
  ActionEventType,
  OpenEntityArgs,
} from '@maximizer/core/shared/ui/config';
import { getFontAwesomeIcon } from '@maximizer/core/shared/ui/icons';
import { UI_KENDO } from '@maximizer/core/shared/ui/kendo';
import {
  ResourceOrTranslatePipe,
  UI_PIPES,
} from '@maximizer/core/shared/ui/pipes';
import { TimelineService } from '@maximizer/core/timeline/data-access';
import { KENDO_BUTTON } from '@progress/kendo-angular-buttons';
import { KENDO_DIALOG } from '@progress/kendo-angular-dialog';
import { KENDO_DROPDOWNLIST } from '@progress/kendo-angular-dropdowns';
import { KENDO_LABEL } from '@progress/kendo-angular-label';
import { KENDO_TABSTRIP } from '@progress/kendo-angular-layout';
import { KENDO_LISTVIEW } from '@progress/kendo-angular-listview';
import { finalize, forkJoin, map, Observable, of, switchMap, tap } from 'rxjs';
import {
  EventAction,
  EventJourney,
  TimelineSummaryEventTracker,
} from '../classes/event-tracker';
import { getTimelineTexts } from '../classes/timeline-event';
import {
  RecommendedAction,
  RefreshArgs,
  Summary,
  TimelineSummaryArgs,
  TimelineSummaryHtmlBuilder,
  TimelineSummaryPrompt,
} from '../classes/timeline-summary';
import { Helper } from '../classes/iqboost';
import { AiModels } from '../classes/ai-models';
import { isNullOrEmpty } from '@maximizer/shared/util';
import { FeedbackComponent } from '../feedback/feedback.component';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { DeAnonymizerPipe } from '../pipes/de-anonymizer.pipe';
import { SafeHtmlPipe } from '../pipes/safe-html.pipe';

const SUMMARY_PROMPT_NAME = 'timeline-summary';

@Component({
  selector: 'ai-timeline-summary',
  templateUrl: './timeline-summary.component.html',
  styleUrl: './timeline-summary.component.scss',
  providers: [ResourceOrTranslatePipe, TimelineSummaryEventTracker],
  imports: [
    DeAnonymizerPipe,
    FeedbackComponent,
    FormsModule,
    SafeHtmlPipe,
    TranslatePipe,
    KENDO_BUTTON,
    KENDO_DIALOG,
    KENDO_DROPDOWNLIST,
    KENDO_LABEL,
    KENDO_LISTVIEW,
    KENDO_TABSTRIP,
    NgClass,
    UI_KENDO,
    UI_LOADER,
    UI_PIPES,
  ],
})
export class TimelineSummaryComponent extends EmbeddedComponent<OpenEntityArgs> {
  @HostBinding('class.ai-ng') croesusClass = true;

  loading = signal(false);
  isOpen = signal(false);
  dueDates = signal([] as TranslationListItem<string>[]);
  summarizeOptions = signal([] as TranslationListItem<string>[]);
  args?: TimelineSummaryArgs;
  summary = signal<Summary | null | undefined>(undefined);
  summaryText = signal('');
  entryName = signal('');
  eventAction = EventAction;
  eventJourney = EventJourney;
  selectedDueDate?: TranslationListItem<string>;
  entryContext = '';
  currentEntry?: Entity;
  sessionInfo?: Session;

  readonly actions: Record<string, RecommendedAction> = {
    Email: {
      icon: getFontAwesomeIcon('envelope', 'solid'),
      entity: 'email',
      translation: {
        resourceID: 'JSS_MnuWriteEmail',
        translationID: 'buttons.newEmail',
      },
    },
    Meeting: {
      icon: getFontAwesomeIcon('calendar'),
      entity: 'appointment',
      translation: {
        resourceID: 'JSS_MnuScheMeeting',
        translationID: 'buttons.newAppointment',
      },
    },
    Task: {
      icon: getFontAwesomeIcon('circle-check', 'solid'),
      entity: 'task',
      translation: {
        resourceID: 'JSS_MnuScheTodo',
        translationID: 'buttons.newTask',
      },
    },
    Call: {
      icon: getFontAwesomeIcon('phone', 'solid'),
      entity: 'interaction',
      translation: {
        resourceID: 'JSS_MnuAbMakecall',
        translationID: 'buttons.makeCall',
      },
      interaction: {
        type: Octopus.DefaultInteractions.CALL,
        icon: 0,
        display: '',
        external: false,
      },
      direction: 'out',
    },
  };
  constructor(
    element: ElementRef,
    private readonly aiFeaturesService: AiFeaturesService,
    private readonly context: ContextService,
    private readonly session: SessionService,
    private readonly timeline: TimelineService,
    private readonly openAi: OpenAiService,
    private readonly piiAnonymization: PiiAnonymizationService,
    private readonly addressBook: AddressBookService,
    private readonly translate: TranslateService,
    private readonly resourceOrTranslate: ResourceOrTranslatePipe,
    private readonly locale: LocaleService,
    private readonly maximizerAi: MaximizerAiService,
    public readonly tracker: TimelineSummaryEventTracker,
    @Inject(CDN_URL) readonly cdn: string,
  ) {
    super(element);
  }

  override doAction(
    name: string,
    args: TimelineSummaryArgs | RefreshArgs,
  ): void {
    if (name === ActionEventType.Show || name === ActionEventType.Initialize) {
      this.args = args as TimelineSummaryArgs;
      this.load();
      this.open();
    } else if (name === ActionEventType.Refresh) {
      const refreshArgs = args as RefreshArgs;
      if (refreshArgs.newKey || refreshArgs.key) {
        this.tracker.trackEvent(
          EventJourney.RecommendedActions,
          EventAction.Executed,
          { type: refreshArgs.type ?? '' },
        );
      }
    } else {
      super.doAction(name, args);
    }
  }

  open(): void {
    this.context.startDialogMode();
    this.isOpen.set(true);
    this.tracker.trackEvent(EventJourney.Summarization, EventAction.Started);
  }

  close(): void {
    this.clear();
    this.context.endDialogMode();
    this.isOpen.set(false);
    this.tracker.trackEvent(EventJourney.Summarization, EventAction.Closed);
  }

  clear(): void {
    this.entryContext = '';
    this.currentEntry = undefined;
    this.entryName.set('');
    this.summary.set(undefined);
    this.summaryText.set('');
  }

  load(): void {
    this.clear();
    this.loadDueDate();
    if (this.args?.key) {
      this.loading.set(true);
      this.addSubscription(
        forkJoin({
          sessionInfo: this.session.getInfo(),
          entry:
            this.args?.type === 'addressBook'
              ? this.addressBook.forReadWithMetadata(this.getKey())
              : of({ value: {}, fields: {} }),
          displayValue: this.addressBook.getDisplayValue(this.getKey()),
        })
          .pipe(
            switchMap((result) =>
              this.aiFeaturesService.processFiltersConfiguration(result.sessionInfo)
                .pipe(
                  map(() => result),
                ),
            ),
            tap(({ sessionInfo, entry, displayValue }) => {
              this.sessionInfo = sessionInfo;
              this.loadEntry(entry.value, entry.fields, displayValue ?? '');
            }),
            switchMap(() => this.summarize()),
          )
          .subscribe(),
      );
    }
  }

  loadEntry(entry: Entity, fields: FieldSet, displayValue: string): void {
    this.currentEntry = entry;

    const getLabel = (field: string): string => fields[field]?.name ?? '';

    const getValue = (field: string): string =>
      (entry[field] as Octopus.ValueAndDisplay<string>)?.DisplayValue ?? '';

    const formatField = (field: string): string => {
      const value = getValue(field);
      return value ? `${getLabel(field)}: "${value}"` : '';
    };

    this.entryName.set(displayValue);
    this.entryContext =
      this.args?.type === 'addressBook'
        ? [
            formatField(Octopus.AbEntryFields.Type),
            formatField(Octopus.AbEntryFields.FullName),
            getValue(Octopus.AbEntryFields.Type) !== Octopus.AbEntryType.Company
              ? formatField(Octopus.AbEntryFields.CompanyName)
              : '',
            formatField(Octopus.AbEntryFields.LastContactDate),
            formatField(Octopus.AbEntryFields.AccountManager),
          ]
            .filter((line) => line.length > 0)
            .join('\n')
        : `${this.args?.type}: "${this.entryName}"`;
  }

  loadDueDate(): void {
    const lastTwoMonths =
      Octopus.DateOptions[Octopus.DateOptionId.THISANDLASTMONTH];

    this.dueDates.set(
      Octopus.getDateOptions([
        Octopus.DateOptionId.THISWEEK,
        Octopus.DateOptionId.LASTWEEK,
        Octopus.DateOptionId.THISMONTH,
        Octopus.DateOptionId.THISANDLASTMONTH,
        Octopus.DateOptionId.LASTMONTH,
        Octopus.DateOptionId.LASTTHREEMONTHS,
        Octopus.DateOptionId.LASTSIXMONTHS,
      ]),
    );

    this.selectedDueDate = lastTwoMonths;
  }

  summarize(): Observable<Summary | null> {
    if (!this.args || !this.selectedDueDate) {
      return of(null);
    }

    const dateOffset = Octopus.getDateOffset(this.selectedDueDate.id);
    this.loading.set(true);

    return forkJoin({
      events: this.timeline.forEntry(this.args.key, this.args.type, {
        ...this.args.filter,
        categories: this.args.filter.showAllCategories
          ? []
          : this.args.filter.categories,
        notes: this.args.filter.notes.filter(
          (note) => note === Octopus.DefaultNotes.MANUAL,
        ),
        customDateQuery:
          typeof dateOffset === 'string'
            ? {
                $EQ: dateOffset,
              }
            : {
                $RANGE: dateOffset,
              },
      }),
      summaryPrompts: this.maximizerAi.lookupPrompts({
        project: 'MaximizerAI',
        filter: { name: [SUMMARY_PROMPT_NAME] },
      }),
      systemPrompts: this.maximizerAi.lookupPrompts({
        project: this.aiFeaturesService.isSLE() ? 'IqBoostSLE' : 'IqBoost',
        filter: {
          name: [
            SYSTEM_PROMPT_ANONYMIZED_CONSIDERATIONS,
            SYSTEM_PROMPT_CHANGE_LANGUAGE,
          ],
          tag: [LANGFUSE_SYSTEM_TAG],
        },
      }),
    }).pipe(
      switchMap(({ events, summaryPrompts, systemPrompts }) => {
        if (events.length) {
          const texts = getTimelineTexts(
            this.resourceOrTranslate,
            this.locale,
            events,
          );
          const prompt = summaryPrompts.find(
            (candidate) => candidate.name === SUMMARY_PROMPT_NAME,
          );
          const languageTextPrompt = systemPrompts.find(
            (candidate) => candidate.name === SYSTEM_PROMPT_CHANGE_LANGUAGE,
          );
          // Lookup returns both system prompts; anonymization gates use, not fetch.
          const anonymizedConsiderationsPrompt = this.piiAnonymization.anonymized()
            ? systemPrompts.find(
                (candidate) =>
                  candidate.name === SYSTEM_PROMPT_ANONYMIZED_CONSIDERATIONS,
              )
            : undefined;
          const config = prompt?.config as
            | OpenAI.RequestConfiguration
            | undefined;
          const contextMessages: OpenAI.GPTMessage[] = [
            { role: 'user', content: this.entryContext },
          ];
          const userMessages: OpenAI.GPTMessage[] = texts.map((content) => ({
            role: 'user',
            content,
          }));
          const systemMessages = [
            Helper.getLanguageSuggestedPrompt(languageTextPrompt),
            anonymizedConsiderationsPrompt?.prompt ?? '',
            prompt?.prompt ?? TimelineSummaryPrompt,
          ];
          const systemPrompt = systemMessages
            .filter((message) => !isNullOrEmpty(message))
            .join('\n\n');
          const requestConfig: OpenAI.RequestConfiguration = config?.model
            ? config
            : {
                model: AiModels.gpt4o,
                max_tokens: 700,
                temperature: 0.1,
                response_format: { type: 'json_object' },
              };

          return this.piiAnonymization.anonymizeContextAndUser(
            { context: contextMessages, user: userMessages, system: [] },
            this.getPiiContext(),
          ).pipe(
            switchMap(({ context, user }) => {
              return this.openAi.summarize<Summary>(
                context.map((message) => message.content),
                user.map((message) => message.content),
                systemPrompt,
                requestConfig,
              );
            }),
          );
        }
        return of(null);
      }),
      tap((summary) => {
        this.setSummary(summary);
        this.tracker.trackEvent(
          EventJourney.Summarization,
          EventAction.Summarized,
        );
      }),
      finalize(() => this.loading.set(false)),
    );
  }

  private getPiiContext(): PIIContext {
    return {
      contacts: Helper.buildPIIContactRecords(
        this.currentEntry ? [this.currentEntry] : [],
        this.sessionInfo,
      ),
    };
  }

  private setSummary(summary: Summary | null): void {
    this.summary.set(summary);
    if (summary) {
      this.summaryText.set(
        new TimelineSummaryHtmlBuilder(summary, this.translate)
          .dealContext()
          .keyMilestoneAndTimelines()
          .highlightsOfPastCommunications()
          .recentCommunicationsSummary()
          .outstandingItemsAndRisks()
          .recommendedActions()
          .sentimentAnalysis()
          .build(),
      );
    } else {
      this.summaryText.set('');
    }
  }

  copyToClipboard(): void {
    if (navigator.clipboard) {
      this.tracker.trackEvent(EventJourney.Action, EventAction.Clicked, {
        name: 'copy-to-clipboard',
      });
      const html = this.piiAnonymization.deAnonymizeHtml(this.summaryText());
      const blob = new Blob([html], { type: 'text/html' });
      const clipboardItem = new ClipboardItem({ 'text/html': blob });
      navigator.clipboard.write([clipboardItem]);
    }
  }

  saveAsNote(): void {
    navigator.clipboard?.writeText(
      this.piiAnonymization.deAnonymizeHtml(this.summaryText()),
    );
    this.actionCompleted.emit(
      new OpenEntityArgs({
        ...this.getEntity('note'),
        autoPaste: true,
      } as NoteEvent),
    );
  }

  add(action: RecommendedAction): void {
    let entity: EntityEvent = this.getEntity(action.entity);

    if (action.interaction) {
      entity = {
        ...entity,
        interaction: action.interaction,
        direction: action.direction,
      } as InteractionEvent;
    }
    this.actionCompleted.emit(new OpenEntityArgs(entity));
    this.tracker.trackEvent(
      EventJourney.RecommendedActions,
      EventAction.Started,
      { type: action.entity },
    );
  }

  private getEntity(type: EntityType): EntityEvent {
    return {
      key: null,
      type,
      with: {
        id: this.getKey() ?? '',
        display: this.entryName(),
        type: 'addressBook',
      },
    };
  }

  private getKey(): string {
    const key = this.args?.key;
    const type = this.args?.type;

    if (!key) {
      return '';
    }

    if (typeof key === 'string') {
      return key;
    } else {
      switch (type) {
        case 'opportunity':
          return key.opportunityKey ?? '';
        case 'case':
          return key.caseKey ?? '';
        case 'lead':
          return key.leadKey ?? '';
        default:
          return key.abEntryKey ?? '';
      }
    }
  }
}
